import id from './id';
import mergeCollections from './mergeCollections';
import type { Collection } from './models/collection';
import type { IndexTask } from './models/indexes';
import { applyIndexJobId, applyIndexSubJobId, type ApplyIndexJob } from './models/job';
import type { State } from './models/state';
import orderBy from './orderBy';

export default function calculateTasks(state: State, collections?: Collection[]): { open: IndexTask[]; planned: IndexTask[] } {
  collections ??= mergeCollections(state).flatMap((database) => database.collections);

  const jobs = Array.from(state.jobs.jobs.values()).filter(
    (job) => job.jobId === applyIndexJobId || job.jobId === applyIndexSubJobId,
  ) as ApplyIndexJob[];

  const plannedIds = new Set(jobs.filter((job) => job.state === 'planned').map((job) => id(job.data)));

  const open = collections.flatMap((collection) => {
    return orderBy(
      collection.indexes
        .flatMap<IndexTask>((index) => {
          const _id = id(index);

          if (!collection.isDefined || index.implicit || plannedIds.has(_id)) {
            return [];
          }

          if (!index.isDefined) {
            return {
              id: _id,
              collection,
              index,
              toCreate: [],
              toUpdate: [],
              toDelete: index.actual,
            };
          }

          const indexIds = new Set(index.actual.map(id));

          const toCreate = collection.actual.filter(
            (actualCollection) =>
              !indexIds.has(
                id({
                  db: actualCollection.db,
                  coll: actualCollection.coll,
                  name: index.name,
                }),
              ),
          );

          const toUpdate = index.actual.filter((actualIndex) => actualIndex.hash !== index.hash);

          return {
            id: _id,
            collection,
            index,
            toCreate,
            toUpdate,
            toDelete: [],
          };
        })
        .filter((index) => !!index.toCreate?.length || !!index.toUpdate?.length || !!index.toDelete?.length),
      [(x) => (x.toDelete?.length ? 0 : x.toUpdate?.length ? 1 : 2)],
    );
  });

  return {
    open,
    planned: jobs.map<IndexTask>((job) => ({
      id: job._id,
      parentId: job.data.parentJobId,
      job,
    })),
  };
}
